/**
 * Pricing Page Event Tracking Module
 *
 * Provides comprehensive analytics tracking for the pricing page user journey.
 * Tracks conversion funnel, discount effectiveness, ROI calculator engagement,
 * package comparison behavior, and abandonment patterns.
 *
 * @module pricingPageEvents
 *
 * @example
 * ```typescript
 * import { PricingPageEventHandler } from '@/modules/pricingPageEvents';
 *
 * // Initialize tracker
 * const tracker = new PricingPageEventHandler();
 *
 * // Track page view
 * tracker.trackPageViewed({
 *   referrer: 'dashboard',
 *   hasActiveDiscount: true
 * });
 *
 * // Track package interaction
 * tracker.trackPackageCardViewed({
 *   packageCode: 'replenishment_basic',
 *   packageName: 'Replenishment Center Basic',
 *   price: 99.00,
 *   isYearly: false
 * });
 * ```
 */

import mixpanel from 'mixpanel-browser';

/**
 * Package data structure for tracking
 */
export interface PackageData {
  packageCode: string;
  packageName: string;
  price: number;
  isYearly: boolean;
  yearlyDiscount?: number;
}

/**
 * Discount data structure for tracking
 */
export interface DiscountData {
  discountCode: string;
  discountType: 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE_MONTHS';
  discountValue: number;
  appliesTo: string; // Package code or 'all'
  isFirstMonthOnly: boolean;
  validUntil?: Date;
}

/**
 * ROI calculator interaction data
 */
export interface ROICalculationData {
  skuCount: number;
  averageOrderValue: number;
  monthlyOrders: number;
  timeSaved: number;
  laborCostSavings: number;
  stockoutPrevention: number;
  annualSavings: number;
}

/**
 * Subscription creation details
 */
export interface SubscriptionDetails {
  packageCode: string;
  packageName: string;
  price: number;
  isYearly: boolean;
  discountApplied: boolean;
  discountCode?: string;
  finalPrice: number;
  subscriptionId: string;
}

/**
 * Page view context
 */
export interface PageViewContext {
  referrer?: string;
  hasActiveDiscount?: boolean;
  discountCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

/**
 * Pricing Page Event Handler
 *
 * Manages all analytics tracking for the pricing page journey.
 * Provides methods for tracking user interactions, conversions, and abandonment.
 */
export class PricingPageEventHandler {
  private sessionId: string;
  private sessionStartTime: number;
  private lastActivityTime: number;
  private viewedPackages: Set<string>;
  private calculatorInteractionCount: number;
  private billingToggleCount: number;

  /**
   * Initialize the pricing page event tracker
   */
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.viewedPackages = new Set<string>();
    this.calculatorInteractionCount = 0;
    this.billingToggleCount = 0;
  }

  // ============================================================================
  // CORE FUNNEL TRACKING
  // ============================================================================

  /**
   * Track pricing page view
   *
   * First touchpoint in the conversion funnel. Records page load with context
   * about how the user arrived and any active promotions.
   *
   * @param context - Page view context including referrer and discount info
   *
   * @example
   * ```typescript
   * tracker.trackPageViewed({
   *   referrer: 'dashboard',
   *   hasActiveDiscount: true,
   *   discountCode: 'FIRST_MONTH_50',
   *   utmSource: 'email',
   *   utmCampaign: 'q4_promotion'
   * });
   * ```
   */
  public trackPageViewed(context: PageViewContext = {}): void {
    this.track('Pricing Page Viewed', {
      sessionId: this.sessionId,
      referrer: context.referrer || document.referrer || 'direct',
      hasActiveDiscount: context.hasActiveDiscount || false,
      discountCode: context.discountCode || null,
      utmSource: context.utmSource || null,
      utmMedium: context.utmMedium || null,
      utmCampaign: context.utmCampaign || null,
      timestamp: new Date().toISOString(),
    });

    // Mark this as first pricing visit for the session
    this.markFirstPricingVisit();
  }

  /**
   * Track package card view
   *
   * Records when a user views (scrolls to) a specific package card.
   * Helps understand which packages attract the most attention.
   *
   * @param packageData - Package information
   *
   * @example
   * ```typescript
   * tracker.trackPackageCardViewed({
   *   packageCode: 'replenishment_plus',
   *   packageName: 'Replenishment Center Plus',
   *   price: 299.00,
   *   isYearly: false
   * });
   * ```
   */
  public trackPackageCardViewed(packageData: PackageData): void {
    // Track only the first view of each package in this session
    if (this.viewedPackages.has(packageData.packageCode)) {
      return;
    }

    this.viewedPackages.add(packageData.packageCode);

    this.track('Package Card Viewed', {
      sessionId: this.sessionId,
      packageCode: packageData.packageCode,
      packageName: packageData.packageName,
      price: packageData.price,
      isYearly: packageData.isYearly,
      yearlyDiscount: packageData.yearlyDiscount || null,
      viewOrder: this.viewedPackages.size, // 1st, 2nd, 3rd package viewed
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track subscribe button click
   *
   * Records when a user clicks the "Subscribe" button on a package card.
   * Key conversion funnel step before actual subscription creation.
   *
   * @param packageData - Package being subscribed to
   * @param discountData - Optional discount being applied
   *
   * @example
   * ```typescript
   * tracker.trackSubscribeButtonClicked(
   *   {
   *     packageCode: 'replenishment_basic',
   *     packageName: 'Replenishment Center Basic',
   *     price: 99.00,
   *     isYearly: false
   *   },
   *   {
   *     discountCode: 'FIRST_MONTH_50',
   *     discountType: 'PERCENTAGE',
   *     discountValue: 50,
   *     appliesTo: 'all',
   *     isFirstMonthOnly: true
   *   }
   * );
   * ```
   */
  public trackSubscribeButtonClicked(
    packageData: PackageData,
    discountData?: DiscountData
  ): void {
    const finalPrice = this.calculateFinalPrice(
      packageData.price,
      discountData
    );
    const discountAmount = discountData
      ? this.calculateDiscountAmount(packageData.price, discountData)
      : 0;

    this.track('Subscribe Button Clicked', {
      sessionId: this.sessionId,
      packageCode: packageData.packageCode,
      packageName: packageData.packageName,
      basePrice: packageData.price,
      isYearly: packageData.isYearly,
      hasDiscount: !!discountData,
      discountCode: discountData?.discountCode || null,
      discountType: discountData?.discountType || null,
      discountValue: discountData?.discountValue || null,
      discountAmount: discountAmount,
      finalPrice: finalPrice,
      totalPackagesViewed: this.viewedPackages.size,
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      timeToDecision: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track successful subscription creation
   *
   * Final conversion event. Records when subscription is successfully created
   * after payment processing.
   *
   * @param subscriptionDetails - Complete subscription information
   *
   * @example
   * ```typescript
   * tracker.trackSubscriptionCreated({
   *   packageCode: 'replenishment_basic',
   *   packageName: 'Replenishment Center Basic',
   *   price: 99.00,
   *   isYearly: false,
   *   discountApplied: true,
   *   discountCode: 'FIRST_MONTH_50',
   *   finalPrice: 49.50,
   *   subscriptionId: 'sub_1234567890'
   * });
   * ```
   */
  public trackSubscriptionCreated(
    subscriptionDetails: SubscriptionDetails
  ): void {
    this.track('Subscription Created', {
      sessionId: this.sessionId,
      packageCode: subscriptionDetails.packageCode,
      packageName: subscriptionDetails.packageName,
      basePrice: subscriptionDetails.price,
      isYearly: subscriptionDetails.isYearly,
      discountApplied: subscriptionDetails.discountApplied,
      discountCode: subscriptionDetails.discountCode || null,
      finalPrice: subscriptionDetails.finalPrice,
      subscriptionId: subscriptionDetails.subscriptionId,
      totalPackagesViewed: this.viewedPackages.size,
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      timeToConversion: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    // Track funnel completion
    this.trackFunnelCompletion(subscriptionDetails);

    this.updateLastActivity();
  }

  /**
   * Track complete funnel journey
   *
   * Internal method that fires when subscription is created.
   * Aggregates all funnel metrics for conversion analysis.
   *
   * @param subscriptionDetails - Subscription information
   * @private
   */
  private trackFunnelCompletion(
    subscriptionDetails: SubscriptionDetails
  ): void {
    this.track('Pricing Funnel Completed', {
      sessionId: this.sessionId,
      packageCode: subscriptionDetails.packageCode,
      isYearly: subscriptionDetails.isYearly,
      discountApplied: subscriptionDetails.discountApplied,
      discountCode: subscriptionDetails.discountCode || null,
      finalPrice: subscriptionDetails.finalPrice,
      // Funnel metrics
      packagesViewed: this.viewedPackages.size,
      calculatorInteractions: this.calculatorInteractionCount,
      billingToggles: this.billingToggleCount,
      timeToConversion: this.getTimeOnPage(),
      // Engagement indicators
      highEngagement:
        this.calculatorInteractionCount > 0 || this.billingToggleCount > 1,
      multiplePackagesConsidered: this.viewedPackages.size > 1,
      timestamp: new Date().toISOString(),
    });
  }

  // ============================================================================
  // DISCOUNT TRACKING
  // ============================================================================

  /**
   * Track discount banner view
   *
   * Records when first-month discount banner is displayed.
   * Helps measure discount visibility and campaign reach.
   *
   * @param discountData - Active discount information
   *
   * @example
   * ```typescript
   * tracker.trackDiscountBannerViewed({
   *   discountCode: 'FIRST_MONTH_50',
   *   discountType: 'PERCENTAGE',
   *   discountValue: 50,
   *   appliesTo: 'all',
   *   isFirstMonthOnly: true,
   *   validUntil: new Date('2024-12-31')
   * });
   * ```
   */
  public trackDiscountBannerViewed(discountData: DiscountData): void {
    this.track('Discount Banner Viewed', {
      sessionId: this.sessionId,
      discountCode: discountData.discountCode,
      discountType: discountData.discountType,
      discountValue: discountData.discountValue,
      appliesTo: discountData.appliesTo,
      isFirstMonthOnly: discountData.isFirstMonthOnly,
      validUntil: discountData.validUntil?.toISOString() || null,
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track discount banner click
   *
   * Records when user clicks on the discount banner to view details.
   *
   * @param discountData - Discount information
   *
   * @example
   * ```typescript
   * tracker.trackDiscountBannerClicked({
   *   discountCode: 'FIRST_MONTH_50',
   *   discountType: 'PERCENTAGE',
   *   discountValue: 50,
   *   appliesTo: 'all',
   *   isFirstMonthOnly: true
   * });
   * ```
   */
  public trackDiscountBannerClicked(discountData: DiscountData): void {
    this.track('Discount Banner Clicked', {
      sessionId: this.sessionId,
      discountCode: discountData.discountCode,
      discountType: discountData.discountType,
      discountValue: discountData.discountValue,
      appliesTo: discountData.appliesTo,
      timeToClick: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track discount validation attempt
   *
   * Records when user manually enters a discount code.
   * Captures both successful and failed validation attempts.
   *
   * @param discountCode - Code entered by user
   * @param isValid - Whether code is valid
   * @param errorMessage - Error message if invalid
   *
   * @example
   * ```typescript
   * // Successful validation
   * tracker.trackDiscountValidation('FIRST_MONTH_50', true);
   *
   * // Failed validation
   * tracker.trackDiscountValidation(
   *   'INVALID_CODE',
   *   false,
   *   'Discount code not found'
   * );
   * ```
   */
  public trackDiscountValidation(
    discountCode: string,
    isValid: boolean,
    errorMessage?: string
  ): void {
    this.track('Discount Code Validated', {
      sessionId: this.sessionId,
      discountCode: discountCode,
      isValid: isValid,
      errorMessage: errorMessage || null,
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  // ============================================================================
  // ROI CALCULATOR TRACKING
  // ============================================================================

  /**
   * Track ROI calculator interaction
   *
   * Records slider adjustments and result calculations.
   * Tracks engagement depth with calculator tool.
   *
   * @param calculationData - Current calculator values and results
   *
   * @example
   * ```typescript
   * tracker.trackROICalculatorInteraction({
   *   skuCount: 2500,
   *   averageOrderValue: 125,
   *   monthlyOrders: 3000,
   *   timeSaved: 15.2,
   *   laborCostSavings: 304,
   *   stockoutPrevention: 3750,
   *   annualSavings: 46660
   * });
   * ```
   */
  public trackROICalculatorInteraction(
    calculationData: ROICalculationData
  ): void {
    this.calculatorInteractionCount++;

    this.track('ROI Calculator Interaction', {
      sessionId: this.sessionId,
      skuCount: calculationData.skuCount,
      averageOrderValue: calculationData.averageOrderValue,
      monthlyOrders: calculationData.monthlyOrders,
      timeSaved: calculationData.timeSaved,
      laborCostSavings: calculationData.laborCostSavings,
      stockoutPrevention: calculationData.stockoutPrevention,
      annualSavings: calculationData.annualSavings,
      interactionNumber: this.calculatorInteractionCount,
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track ROI calculator scroll into view
   *
   * Records when calculator becomes visible on screen.
   * Helps measure calculator visibility and positioning effectiveness.
   *
   * @example
   * ```typescript
   * tracker.trackROICalculatorViewed();
   * ```
   */
  public trackROICalculatorViewed(): void {
    this.track('ROI Calculator Viewed', {
      sessionId: this.sessionId,
      timeToView: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  // ============================================================================
  // PACKAGE COMPARISON TRACKING
  // ============================================================================

  /**
   * Track billing toggle interaction
   *
   * Records when user toggles between monthly and yearly billing.
   * Helps understand pricing comparison behavior.
   *
   * @param selectedBillingPeriod - 'monthly' or 'yearly'
   * @param packagesOnScreen - Number of packages visible
   *
   * @example
   * ```typescript
   * tracker.trackBillingToggle('yearly', 3);
   * ```
   */
  public trackBillingToggle(
    selectedBillingPeriod: 'monthly' | 'yearly',
    packagesOnScreen: number
  ): void {
    this.billingToggleCount++;

    this.track('Billing Period Toggled', {
      sessionId: this.sessionId,
      selectedBillingPeriod: selectedBillingPeriod,
      toggleNumber: this.billingToggleCount,
      packagesOnScreen: packagesOnScreen,
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track package comparison behavior
   *
   * Records when user views multiple packages, indicating comparison.
   * Fires after viewing 2+ packages within short time window.
   *
   * @param packages - List of packages being compared
   *
   * @example
   * ```typescript
   * tracker.trackPackageComparison([
   *   { packageCode: 'replenishment_basic', packageName: 'Basic', price: 99 },
   *   { packageCode: 'replenishment_plus', packageName: 'Plus', price: 299 }
   * ]);
   * ```
   */
  public trackPackageComparison(
    packages: Array<{ packageCode: string; packageName: string; price: number }>
  ): void {
    this.track('Packages Compared', {
      sessionId: this.sessionId,
      packageCount: packages.length,
      packages: packages.map((p) => ({
        code: p.packageCode,
        name: p.packageName,
        price: p.price,
      })),
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  // ============================================================================
  // ABANDONMENT & PERFORMANCE TRACKING
  // ============================================================================

  /**
   * Track exit intent
   *
   * Records when user shows intent to leave page (mouse leaves viewport).
   * Useful for identifying friction points and potential abandonment.
   *
   * @param packagesViewed - Number of packages viewed before exit intent
   * @param lastInteraction - Type of last interaction before exit intent
   *
   * @example
   * ```typescript
   * tracker.trackExitIntent(2, 'package_card_view');
   * ```
   */
  public trackExitIntent(
    packagesViewed: number,
    lastInteraction?: string
  ): void {
    this.track('Exit Intent Detected', {
      sessionId: this.sessionId,
      packagesViewed: packagesViewed,
      lastInteraction: lastInteraction || 'unknown',
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track scroll depth
   *
   * Records how far down the page user scrolls.
   * Helps understand content engagement and scroll-based visibility.
   *
   * @param percentScrolled - Percentage of page scrolled (0-100)
   *
   * @example
   * ```typescript
   * tracker.trackScrollDepth(75); // Scrolled 75% down page
   * ```
   */
  public trackScrollDepth(percentScrolled: number): void {
    // Only track milestone percentages: 25%, 50%, 75%, 100%
    const milestones = [25, 50, 75, 100];
    if (!milestones.includes(percentScrolled)) {
      return;
    }

    this.track('Scroll Depth Reached', {
      sessionId: this.sessionId,
      percentScrolled: percentScrolled,
      timeToReach: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track page load performance
   *
   * Records page load timing metrics.
   * Helps correlate performance with conversion rates.
   *
   * @param loadTime - Page load time in milliseconds
   * @param performanceMetrics - Additional performance metrics
   *
   * @example
   * ```typescript
   * tracker.trackPagePerformance(1250, {
   *   domContentLoaded: 890,
   *   firstPaint: 450,
   *   firstContentfulPaint: 520
   * });
   * ```
   */
  public trackPagePerformance(
    loadTime: number,
    performanceMetrics?: {
      domContentLoaded?: number;
      firstPaint?: number;
      firstContentfulPaint?: number;
    }
  ): void {
    this.track('Page Performance Measured', {
      sessionId: this.sessionId,
      loadTime: loadTime,
      performanceCategory: this.getPerformanceCategory(loadTime),
      domContentLoaded: performanceMetrics?.domContentLoaded || null,
      firstPaint: performanceMetrics?.firstPaint || null,
      firstContentfulPaint: performanceMetrics?.firstContentfulPaint || null,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track session abandonment
   *
   * Records when user leaves without subscribing.
   * Should be called on beforeunload or visibility change events.
   *
   * @param reason - Reason for abandonment ('page_close', 'navigation', 'timeout')
   *
   * @example
   * ```typescript
   * // In component beforeDestroy
   * tracker.trackSessionAbandoned('navigation');
   * ```
   */
  public trackSessionAbandoned(reason: string): void {
    // Only track if user was active (viewed at least one package)
    if (this.viewedPackages.size === 0) {
      return;
    }

    this.track('Pricing Session Abandoned', {
      sessionId: this.sessionId,
      reason: reason,
      packagesViewed: this.viewedPackages.size,
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      timeSpent: this.getTimeOnPage(),
      timeSinceLastActivity: Math.round(
        (Date.now() - this.lastActivityTime) / 1000
      ),
      timestamp: new Date().toISOString(),
    });
  }

  // ============================================================================
  // PAYMENT CONFIRMATION TRACKING (Critical for TRUE Conversion)
  // ============================================================================

  /**
   * Track redirect to Shopify payment page
   *
   * Records when user is redirected to Shopify to approve payment.
   * This happens AFTER subscription is created with "CONFIRMING" status.
   *
   * @param subscriptionDetails - Subscription information
   * @param confirmationUrl - Shopify confirmation URL
   *
   * @example
   * ```typescript
   * tracker.trackPaymentRedirect({
   *   packageCode: 'replenishment_basic',
   *   packageName: 'Replenishment Center Basic',
   *   price: 99.00,
   *   isYearly: false,
   *   discountApplied: true,
   *   finalPrice: 49.50,
   *   subscriptionId: 'pending'
   * }, 'https://shopify.com/charge/...');
   * ```
   */
  public trackPaymentRedirect(
    subscriptionDetails: {
      packageCode: string;
      packageName: string;
      price: number;
      isYearly: boolean;
      discountApplied: boolean;
      discountCode?: string;
      finalPrice: number;
      subscriptionId: string;
    },
    confirmationUrl: string
  ): void {
    this.track('Payment Redirect - Sent to Shopify', {
      sessionId: this.sessionId,
      packageCode: subscriptionDetails.packageCode,
      packageName: subscriptionDetails.packageName,
      basePrice: subscriptionDetails.price,
      isYearly: subscriptionDetails.isYearly,
      discountApplied: subscriptionDetails.discountApplied,
      discountCode: subscriptionDetails.discountCode || null,
      finalPrice: subscriptionDetails.finalPrice,
      subscriptionId: subscriptionDetails.subscriptionId,
      confirmationUrlProvided: !!confirmationUrl,
      timeToRedirect: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    // The page leaves for Shopify billing right after; a batched event leaves with it (AS-11622).
    }, { transport: 'sendBeacon', send_immediately: true });

    this.updateLastActivity();
  }

  /**
   * Track return from Shopify payment page
   *
   * Records when user returns from Shopify with payment approval.
   * This happens when user lands on /subscription/confirm page with charge_id.
   *
   * @param chargeId - Shopify charge ID from callback URL
   * @param subscriptionId - Internal subscription ID (if known)
   *
   * @example
   * ```typescript
   * tracker.trackPaymentReturn(123456, 'sub_123');
   * ```
   */
  public trackPaymentReturn(chargeId: number, subscriptionId?: string): void {
    this.track('Payment Approved - Returned from Shopify', {
      sessionId: this.sessionId,
      chargeId: chargeId,
      subscriptionId: subscriptionId || null,
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track successful subscription confirmation
   *
   * **CRITICAL EVENT** - This is the TRUE CONVERSION point!
   * Records when subscription status changes from "CONFIRMING" to "ACTIVE"
   * after Shopify payment approval and backend confirmation.
   *
   * This is the final step in the conversion funnel and represents
   * an actual paying customer, not just intent.
   *
   * @param subscriptionDetails - Complete subscription information
   * @param confirmationDetails - Confirmation process details
   *
   * @example
   * ```typescript
   * tracker.trackSubscriptionConfirmed({
   *   packageCode: 'replenishment_basic',
   *   packageName: 'Replenishment Center Basic',
   *   price: 99.00,
   *   isYearly: false,
   *   discountApplied: true,
   *   discountCode: 'FIRST_MONTH_50',
   *   finalPrice: 49.50,
   *   billingPackageId: 1
   * }, {
   *   chargeId: 123456,
   *   subscriptionId: 'sub_123',
   *   retryCount: 0,
   *   confirmationTimeMs: 2000
   * });
   * ```
   */
  public trackSubscriptionConfirmed(
    subscriptionDetails: {
      packageCode: string;
      packageName: string;
      price: number;
      isYearly: boolean;
      discountApplied: boolean;
      discountCode?: string;
      finalPrice: number;
      billingPackageId: number;
    },
    confirmationDetails: {
      chargeId: number;
      subscriptionId: string;
      retryCount: number;
      confirmationTimeMs: number;
    }
  ): void {
    this.track('Subscription Confirmed - TRUE CONVERSION', {
      sessionId: this.sessionId,
      // Package details
      packageCode: subscriptionDetails.packageCode,
      packageName: subscriptionDetails.packageName,
      basePrice: subscriptionDetails.price,
      isYearly: subscriptionDetails.isYearly,
      discountApplied: subscriptionDetails.discountApplied,
      discountCode: subscriptionDetails.discountCode || null,
      finalPrice: subscriptionDetails.finalPrice,
      billingPackageId: subscriptionDetails.billingPackageId,
      // Confirmation details
      chargeId: confirmationDetails.chargeId,
      subscriptionId: confirmationDetails.subscriptionId,
      retryCount: confirmationDetails.retryCount,
      confirmationTimeMs: confirmationDetails.confirmationTimeMs,
      // Engagement metrics from session
      totalPackagesViewed: this.viewedPackages.size,
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      // Timing
      timeToTrueConversion: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    // Track complete funnel with TRUE conversion
    this.trackTrueFunnelCompletion(subscriptionDetails);

    this.updateLastActivity();
  }

  /**
   * Track complete funnel journey to TRUE conversion
   *
   * Internal method that fires when subscription is confirmed (ACTIVE status).
   * This represents the complete journey from page view to paying customer.
   *
   * @param subscriptionDetails - Subscription information
   * @private
   */
  private trackTrueFunnelCompletion(
    subscriptionDetails: {
      packageCode: string;
      isYearly: boolean;
      discountApplied: boolean;
      discountCode?: string;
      finalPrice: number;
    }
  ): void {
    this.track('Pricing Funnel - Complete to Active Subscriber', {
      sessionId: this.sessionId,
      packageCode: subscriptionDetails.packageCode,
      isYearly: subscriptionDetails.isYearly,
      discountApplied: subscriptionDetails.discountApplied,
      discountCode: subscriptionDetails.discountCode || null,
      finalPrice: subscriptionDetails.finalPrice,
      // Complete funnel metrics
      packagesViewed: this.viewedPackages.size,
      calculatorInteractions: this.calculatorInteractionCount,
      billingToggles: this.billingToggleCount,
      timeToActiveSubscriber: this.getTimeOnPage(),
      // Engagement quality
      highEngagement:
        this.calculatorInteractionCount > 0 || this.billingToggleCount > 1,
      multiplePackagesConsidered: this.viewedPackages.size > 1,
      // Conversion attribution
      conversionType: subscriptionDetails.discountApplied
        ? 'discount_conversion'
        : 'organic_conversion',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track subscription confirmation failure
   *
   * Records when payment approval succeeds but backend confirmation fails
   * after returning from Shopify. This is critical for identifying technical issues.
   *
   * @param failureDetails - Details about the confirmation failure
   *
   * @example
   * ```typescript
   * tracker.trackSubscriptionConfirmationFailed({
   *   chargeId: 123456,
   *   errorMessage: 'Max retries reached',
   *   retryCount: 10,
   *   lastErrorCode: 'TIMEOUT'
   * });
   * ```
   */
  public trackSubscriptionConfirmationFailed(
    failureDetails: {
      chargeId: number;
      errorMessage: string;
      retryCount: number;
      lastErrorCode?: string;
    }
  ): void {
    this.track('Subscription Confirmation Failed', {
      sessionId: this.sessionId,
      chargeId: failureDetails.chargeId,
      errorMessage: failureDetails.errorMessage,
      retryCount: failureDetails.retryCount,
      lastErrorCode: failureDetails.lastErrorCode || 'UNKNOWN',
      timeOnPage: this.getTimeOnPage(),
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track confirmation retry attempt
   *
   * Records each retry attempt when initial confirmation fails.
   * Helps understand reliability of confirmation endpoint.
   *
   * @param chargeId - Shopify charge ID
   * @param attemptNumber - Current retry attempt (1-10)
   *
   * @example
   * ```typescript
   * tracker.trackConfirmationRetry(123456, 3);
   * ```
   */
  public trackConfirmationRetry(chargeId: number, attemptNumber: number): void {
    this.track('Subscription Confirmation Retry', {
      sessionId: this.sessionId,
      chargeId: chargeId,
      attemptNumber: attemptNumber,
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  // ============================================================================
  // FREE PLAN INTERCEPTION TRACKING
  // ============================================================================

  /**
   * Track free plan interception dialog shown
   *
   * Records when interception dialog is displayed to encourage trial signup.
   * Key metric for measuring soft conversion tactics effectiveness.
   *
   * @example
   * ```typescript
   * tracker.trackFreePlanInterceptionShown();
   * ```
   */
  public trackFreePlanInterceptionShown(): void {
    this.track('Free Plan Interception Dialog Shown', {
      sessionId: this.sessionId,
      timeOnPage: this.getTimeOnPage(),
      packagesViewed: this.viewedPackages.size,
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track user choice from free plan interception dialog
   *
   * Records whether user chose trial or insisted on free plan.
   * Critical metric for measuring interception effectiveness.
   *
   * @param choice - 'trial' if user chose trial, 'free' if insisted on free
   *
   * @example
   * ```typescript
   * // User chose to try paid trial
   * tracker.trackFreePlanInterceptionConverted('trial');
   *
   * // User insisted on free plan
   * tracker.trackFreePlanInterceptionConverted('free');
   * ```
   */
  public trackFreePlanInterceptionConverted(choice: 'trial' | 'free'): void {
    this.track('Free Plan Interception Converted', {
      sessionId: this.sessionId,
      userChoice: choice,
      convertedToTrial: choice === 'trial',
      timeToDecision: this.getTimeOnPage(),
      packagesViewed: this.viewedPackages.size,
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  /**
   * Track free plan interception dialog dismissal
   *
   * Records when user closes dialog without choosing either option.
   * Indicates potential friction or unclear messaging.
   *
   * @example
   * ```typescript
   * tracker.trackFreePlanInterceptionDismissed();
   * ```
   */
  public trackFreePlanInterceptionDismissed(): void {
    this.track('Free Plan Interception Dialog Dismissed', {
      sessionId: this.sessionId,
      timeOnPage: this.getTimeOnPage(),
      packagesViewed: this.viewedPackages.size,
      calculatorInteracted: this.calculatorInteractionCount > 0,
      billingToggled: this.billingToggleCount > 0,
      timestamp: new Date().toISOString(),
    });

    this.updateLastActivity();
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Send event to Mixpanel
   *
   * Wrapper around mixpanel.track() with error handling.
   *
   * @param eventName - Name of the event
   * @param properties - Event properties
   * @private
   */
  private track(eventName: string, properties: any, options?: { transport?: 'sendBeacon' | 'xhr'; send_immediately?: boolean }): void {
    try {
      mixpanel.track(eventName, properties, options);
    } catch (error) {
      console.error(`[PricingPageEvents] Error tracking "${eventName}":`, error);
    }
  }

  /**
   * Get or create session ID
   *
   * Creates a unique session ID for this pricing page visit.
   * Stored in sessionStorage to persist across page reloads.
   *
   * @returns Session ID (UUID v4)
   * @private
   */
  private getOrCreateSessionId(): string {
    const storageKey = 'pricing_session_id';
    let sessionId = sessionStorage.getItem(storageKey);

    if (!sessionId) {
      // Generate session ID using same pattern as reportBuilderEvents.ts
      sessionId = `pricing_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(storageKey, sessionId);
    }

    // TypeScript: sessionId is guaranteed to be a string here
    return sessionId as string;
  }

  /**
   * Calculate time spent on page
   *
   * @returns Time in seconds since page load
   * @private
   */
  private getTimeOnPage(): number {
    return Math.round((Date.now() - this.sessionStartTime) / 1000);
  }

  /**
   * Update last activity timestamp
   *
   * @private
   */
  private updateLastActivity(): void {
    this.lastActivityTime = Date.now();
  }

  /**
   * Check if this is the first pricing page visit
   *
   * @returns True if first visit in this session
   * @private
   */
  private isFirstPricingVisit(): boolean {
    return !sessionStorage.getItem('pricing_page_visited');
  }

  /**
   * Mark pricing page as visited
   *
   * @private
   */
  private markFirstPricingVisit(): void {
    sessionStorage.setItem('pricing_page_visited', 'true');
  }

  /**
   * Calculate discount amount in dollars
   *
   * @param basePrice - Original package price
   * @param discountData - Discount information
   * @returns Discount amount in dollars
   * @private
   */
  private calculateDiscountAmount(
    basePrice: number,
    discountData?: DiscountData
  ): number {
    if (!discountData) {
      return 0;
    }

    if (discountData.discountType === 'PERCENTAGE') {
      return Math.round((basePrice * discountData.discountValue / 100) * 100) / 100;
    } else if (discountData.discountType === 'FIXED_AMOUNT') {
      return discountData.discountValue;
    } else if (discountData.discountType === 'FREE_MONTHS') {
      // For first month free, discount = full price
      return discountData.isFirstMonthOnly ? basePrice : 0;
    }

    return 0;
  }

  /**
   * Calculate final price after discount
   *
   * @param basePrice - Original package price
   * @param discountData - Discount information
   * @returns Final price in dollars
   * @private
   */
  private calculateFinalPrice(
    basePrice: number,
    discountData?: DiscountData
  ): number {
    const discountAmount = this.calculateDiscountAmount(basePrice, discountData);
    return Math.max(0, Math.round((basePrice - discountAmount) * 100) / 100);
  }

  /**
   * Get performance category for load time
   *
   * Categorizes page load performance for analysis.
   *
   * @param loadTime - Page load time in milliseconds
   * @returns Performance category
   * @private
   */
  private getPerformanceCategory(loadTime: number): string {
    if (loadTime < 1000) return 'fast';
    if (loadTime < 3000) return 'medium';
    return 'slow';
  }
}
